import { useTranslations } from '@aikoa/i18n/react'
import { useMemo } from 'react'
import { View } from 'react-native'
import ContentBox, { BoxActionsHeader } from '~/components/layout/ContentBox'
import { ViewTitle } from '~/components/layout/Sections'
import { Body, Label } from '~/components/typography/Content'
import { LinkButton } from '~/components/ui/button'
import { InfoAlert } from '~/components/ui/info-alert'
import {
  ContactMethodsManager,
  type ContactMethodsChangeEvent,
} from '~/components/user/ContactMethodsManager'
import { useSession } from '~/components/user/SessionProvider'
import { useOrganisationCandidate } from '~/data/user/organisationCandidate'
import { useUpdateUserProfile, useUserProfile } from '~/data/user/userProfile'

export function OrganisationBox({
  className,
  showOnlyForUnverifiedEmail = false,
}: {
  className?: string
  showOnlyForUnverifiedEmail?: boolean
}) {
  const { t } = useTranslations('user')

  const { data: profile } = useUserProfile()
  const { session } = useSession()
  const user = session?.user

  const updateUserProfileMutation = useUpdateUserProfile()

  const handleOrganisationEmailChange = (values: ContactMethodsChangeEvent) => {
    if (!profile?.user_id) {
      return
    }

    updateUserProfileMutation.mutate({
      user_id: profile.user_id,
      email: values.email,
    })
  }

  const organisation = profile?.organisation
  const { data: candidateOrganisationRaw } = useOrganisationCandidate()

  const candidateOrganisation =
    candidateOrganisationRaw && 'id' in candidateOrganisationRaw ? candidateOrganisationRaw : null

  const emailState = useMemo(() => {
    if (!user) {
      return null
    }

    const hasEmail = !!user.email
    const hasNewEmail = !!user.new_email
    const isEmailConfirmed = !!user.email_confirmed_at

    if (organisation) {
      return 'verified_with_org'
    }

    if (!hasEmail && !hasNewEmail) {
      return 'no_email'
    }

    if (hasNewEmail || (hasEmail && !isEmailConfirmed)) {
      return 'email_not_verified'
    }

    if (isEmailConfirmed && !organisation) {
      return 'verified_no_org'
    }

    return null
  }, [user, organisation])

  if (
    showOnlyForUnverifiedEmail &&
    ['verified_with_org', 'verified_no_org'].includes(emailState ?? '')
  ) {
    return null
  }

  return (
    <ContentBox
      scrollable={false}
      className={className}
      contentContainerClassName="gap-8 px-0 pt-0"
      header={
        <BoxActionsHeader variant="default">
          <ViewTitle>{t(($) => $.profile.realtor.organisation.title)}</ViewTitle>
          <Body className="text-pretty text-center text-secondary">
            {t(($) => $.profile.realtor.organisation.description)}
          </Body>
        </BoxActionsHeader>
      }>
      <View className="gap-4 px-6">
        {emailState === 'verified_with_org' && organisation ? (
          <View className="gap-1">
            <Label className="font-semibold text-lg">{organisation.name}</Label>
            <Body className="text-sm text-secondary">{organisation.email_domain}</Body>
          </View>
        ) : emailState === 'no_email' ? (
          <InfoAlert level="info">{t(($) => $.profile.realtor.organisation.noEmail)}</InfoAlert>
        ) : emailState === 'email_not_verified' ? (
          <>
            {candidateOrganisation && (
              <View className="gap-1">
                <Label className="font-semibold text-lg">{candidateOrganisation.name}</Label>
                <Body className="text-sm">
                  {t(($) => $.profile.realtor.organisation.verifiedWithOrg)}
                </Body>
              </View>
            )}
            <InfoAlert level="warning">
              {t(($) => $.profile.realtor.organisation.emailNotVerified)}
            </InfoAlert>
          </>
        ) : emailState === 'verified_no_org' ? (
          <>
            <InfoAlert level="info">
              {t(($) => $.profile.realtor.organisation.verifiedNoOrg)}
            </InfoAlert>
            <LinkButton href="mailto:asiakaspalvelu@aikoa.ai" size="lg" variant="default">
              {t(($) => $.profile.realtor.organisation.contactSupport)}
            </LinkButton>
          </>
        ) : (
          <InfoAlert level="info">{t(($) => $.profile.realtor.organisation.noOrg)}</InfoAlert>
        )}
      </View>
      <ContactMethodsManager
        onChange={handleOrganisationEmailChange}
        showEmail
        showPhone={false}
        className="px-6"
      />
      {!!user?.email && (
        <Body className="text-pretty px-6 text-center text-secondary">
          {t(($) => $.profile.realtor.organisation.loginInfo)}
        </Body>
      )}
    </ContentBox>
  )
}
